import type { ReactNode } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import { Typography } from '@gto/ui/typography'

import type { AffiliateCampaignRowSchema } from '@/domain/campaigns'
import { codesTouched, funnelRatePct, unbilledRedemptions } from '@/domain/campaigns'

/**
 * The part of a column that did NOT reach the next step.
 *
 * Deliberately a near-transparent ghost rather than a solid muted fill: column
 * HEIGHT is the most salient thing the eye reads, and a solid cap made a
 * 12-of-400 conversion look like a tall column labelled "Converted 12". At this
 * weight the solid segment is unambiguously the value and the cap reads as the
 * room it had to fill.
 */
const DROPPED_CLASS_NAME = 'bg-text-hover'

type ColumnSegment = {
  /** Stable identity for the segment (used as the React key). */
  id: string
  value: number
  className: string
}

type FunnelColumnProps = {
  label: ReactNode
  /** Which window the figure belongs to — the all-time / in-range split this view must not blur. */
  scope: ReactNode
  value: number
  /**
   * Column height as a share of the funnel's tallest step (0–100). The column
   * IS the previous step, so the stack shortens across the chart and each
   * column's fill can be read against the figure its rate quotes.
   */
  heightPct: number
  /** Denominator for the segment heights — the previous step's count. */
  columnValue: number
  /** Ordered TOP-first, since the stack grows from the baseline upward. */
  segments: ColumnSegment[]
  /** Step-over-previous-step conversion. `null` renders a dash (no denominator). */
  ratePct?: number | null
  tooltip?: string
  'data-tst'?: string
}

const FunnelColumn = ({
  label,
  scope,
  value,
  heightPct,
  columnValue,
  segments,
  ratePct,
  tooltip,
  'data-tst': dataTst,
}: FunnelColumnProps) => (
  <div
    className="flex min-w-0 flex-1 flex-col gap-2"
    data-tst={dataTst}
  >
    {/* Fixed height so the columns' baselines and captions line up whether or
        not a step carries a rate (the first one does not). */}
    <div className="flex h-12 flex-col gap-0.5">
      <Typography
        className="font-heading font-semibold tabular-nums leading-100"
        variant="h6"
      >
        <FormattedNumber value={value} />
      </Typography>
      {ratePct !== undefined && (
        <Typography
          className="tabular-nums leading-120"
          color="text.secondary"
          variant="caption"
        >
          {ratePct === null ? (
            '—'
          ) : (
            <FormattedMessage
              id="admin.partnerDetail.campaigns.detail.ofPrevious"
              values={{
                pct: (
                  <FormattedNumber
                    maximumFractionDigits={1}
                    value={ratePct}
                  />
                ),
              }}
            />
          )}
        </Typography>
      )}
    </div>

    {/* Decorative: every figure the column encodes is already in the text
        around it. There is deliberately no full-height track behind the stack —
        the stack shortening IS the funnel shape, and a constant-height backdrop
        would flatten it straight back out. */}
    <div
      aria-hidden
      className="flex h-40 flex-col justify-end"
    >
      <div
        className="flex flex-col justify-end overflow-hidden rounded-t-xs"
        data-tst="funnel-lane"
        style={{ height: `${heightPct}%` }}
      >
        {segments.map((segment) => (
          <div
            key={segment.id}
            data-tst={`funnel-${segment.id}`}
            className={cn(
              'w-full',
              segment.className,
              // A real but tiny count must not round away to nothing: one code
              // out of twelve hundred is still a code that happened.
              segment.value > 0 && 'min-h-[3px]',
            )}
            style={{
              height: `${columnValue > 0 ? Math.min(100, (segment.value / columnValue) * 100) : 0}%`,
            }}
          />
        ))}
      </div>
    </div>

    <div className="flex flex-col gap-0.5 border-t border-divider pt-2">
      <div className="flex min-w-0 items-center gap-1">
        <Typography
          className="truncate font-medium"
          variant="body2"
        >
          {label}
        </Typography>
        {tooltip != null && (
          <HelpIconTooltip
            content={tooltip}
            size="xs"
          />
        )}
      </div>
      <Typography
        className="truncate"
        color="text.secondary"
        variant="caption"
      >
        {scope}
      </Typography>
    </div>
  </div>
)

type LegendDotProps = {
  className: string
  label: ReactNode
  value: number
  tooltip?: string
}

const LegendDot = ({ className, label, value, tooltip }: LegendDotProps) => (
  <div className="flex items-center gap-1.5">
    <span
      aria-hidden
      className={cn('size-2 shrink-0 rounded-full', className)}
    />
    <Typography
      className="tabular-nums"
      variant="body2"
    >
      <FormattedNumber value={value} />
    </Typography>
    <Typography
      color="text.secondary"
      variant="body2"
    >
      {label}
    </Typography>
    {tooltip != null && (
      <HelpIconTooltip
        content={tooltip}
        size="xs"
      />
    )}
  </div>
)

type NoteProps = {
  messageId:
    | 'admin.partnerDetail.campaigns.detail.abandonedNote'
    | 'admin.partnerDetail.campaigns.detail.inFlightNote'
  tooltipId:
    | 'admin.partnerDetail.campaigns.detail.abandonedTip'
    | 'admin.partnerDetail.campaigns.detail.inFlightTip'
  count: number
}

const Note = ({ messageId, tooltipId, count }: NoteProps) => {
  const { formatMessage } = useIntl()

  return (
    <div className="flex items-center gap-1.5">
      <Typography
        className="tabular-nums"
        color="text.secondary"
        variant="body2"
      >
        <FormattedMessage
          id={messageId}
          values={{ count: <FormattedNumber value={count} /> }}
        />
      </Typography>
      <HelpIconTooltip
        content={formatMessage({ id: tooltipId })}
        size="xs"
      />
    </div>
  )
}

const UNBILLED_LABEL_ID = {
  free: 'admin.partnerDetail.campaigns.detail.unbilled.free',
  refunded: 'admin.partnerDetail.campaigns.detail.unbilled.refunded',
  unattributed: 'admin.partnerDetail.campaigns.detail.unbilled.unattributed',
  awaiting: 'admin.partnerDetail.campaigns.detail.unbilled.awaiting',
} as const

/**
 * Why redemptions in this range earned nothing.
 *
 * Without it, "converted 1 / $0 revenue" reads as a bug on a screen that cannot
 * say otherwise: the funnel is written synchronously at checkout while the money
 * is written by a later async task, so the two halves legitimately disagree for
 * a free order, a refund, an unattributed payment, or a commission that has not
 * landed yet. Renders only when a redemption actually went unbilled — a fully
 * billed campaign has nothing to explain and gets no chrome.
 */
type UnbilledRedemptionsProps = {
  row: AffiliateCampaignRowSchema
  partnerTierMissing?: boolean
}

const UnbilledRedemptions = ({ row, partnerTierMissing }: UnbilledRedemptionsProps) => {
  const reasons = unbilledRedemptions(row.redemption_outcomes)

  if (reasons.length === 0) {
    return null
  }

  return (
    <div
      className="flex w-full flex-col gap-1 border-t border-divider pt-3"
      data-tst="campaign-unbilled"
    >
      <Typography
        color="text.secondary"
        variant="body2"
      >
        <FormattedMessage
          id="admin.partnerDetail.campaigns.detail.unbilled.heading"
          values={{ orders: <FormattedNumber value={row.orders} /> }}
        />
      </Typography>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {reasons.map(({ id, count }) => (
          <Typography
            key={id}
            className="tabular-nums"
            data-tst={`campaign-unbilled-${id}`}
            variant="body2"
          >
            <FormattedMessage
              id={UNBILLED_LABEL_ID[id]}
              values={{ count: <FormattedNumber value={count} /> }}
            />
          </Typography>
        ))}
      </div>
      {partnerTierMissing === true && (
        <Typography
          color="warning"
          data-tst="campaign-tier-missing"
          variant="body2"
        >
          <FormattedMessage id="admin.partnerDetail.campaigns.detail.unbilled.tierMissing" />
        </Typography>
      )}
    </div>
  )
}

/**
 * The campaign's redemption funnel as three descending columns.
 *
 * Vertical rather than the stacked horizontal bars this started as: a funnel's
 * value is the descent being perceptible at a glance, and side-by-side columns
 * give that away as a silhouette instead of asking the reader to compare three
 * bar lengths across the full width of the card.
 *
 * Each column's STACK is the previous step and its fill is this step, so the
 * bar shows exactly what the "% of previous step" caption claims — a campaign
 * that converted every code it attempted renders as a full column, however few
 * codes that was. Scaling every column to `codes_minted` instead (the obvious
 * first approach) draws a perfect 1-of-1 conversion as a stub that reads as a
 * failure.
 *
 * The muted cap on each stack is the drop-off, so the empty space carries the
 * campaign's most important fact rather than reading as a missing bar. Only the
 * converted step keeps the primary accent.
 */
type CampaignFunnelProps = {
  row: AffiliateCampaignRowSchema
  partnerTierMissing?: boolean
}

export const CampaignFunnel = ({ row, partnerTierMissing }: CampaignFunnelProps) => {
  const { formatMessage } = useIntl()

  // Column heights are shares of the TALLEST step, not `codes_minted` alone:
  // minted is all-time while the funnel steps are range-scoped, so a BE that
  // ever reported more attempts than mints would otherwise overflow the chart.
  const base = Math.max(row.codes_minted, row.codes_attempted, row.codes_converted, 1)
  const share = (value: number) => Math.min(100, (value / base) * 100)

  const touched = codesTouched(row)

  // Each column holds the LARGER of its own step and the step it feeds, so a
  // BE-drifted step that exceeds its predecessor (more attempts than mints; a
  // code converting twice) still renders inside a stack tall enough to hold it
  // instead of collapsing to a zero-height, invisible column. The rate captions
  // keep reporting the real, uncapped figures.
  const attemptColumn = Math.max(row.codes_minted, row.codes_attempted)
  const convertColumn = Math.max(row.codes_attempted, row.codes_converted)

  return (
    <div
      className="flex flex-wrap items-start gap-x-10 gap-y-4"
      data-tst="campaign-funnel"
    >
      {/* Capped rather than stretched across the card: three columns spread
          over ~1900px encode three small integers and lose the silhouette that
          is the entire point of drawing them. */}
      <div className="flex w-[320px] max-w-full shrink-0 items-stretch gap-3">
        <FunnelColumn
          columnValue={row.codes_minted}
          data-tst="campaign-inventory"
          heightPct={share(row.codes_minted)}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.detail.minted" />}
          scope={<FormattedMessage id="admin.partnerDetail.campaigns.detail.allTime" />}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.mintedTip' })}
          value={row.codes_minted}
          segments={[
            // `codes_unused` and `codes_minted` are both all-time, so their
            // difference is the one segmentation of the minted pool that stays
            // sound once a date filter is applied. Muted on top, advanced below.
            { id: 'unused', value: row.codes_unused, className: DROPPED_CLASS_NAME },
            { id: 'touched', value: touched, className: 'bg-text-disabled' },
          ]}
        />

        <FunnelColumn
          columnValue={attemptColumn}
          data-tst="funnel-row-attempted"
          heightPct={share(attemptColumn)}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.attempted" />}
          ratePct={funnelRatePct(row.codes_attempted, row.codes_minted)}
          scope={<FormattedMessage id="admin.partnerDetail.campaigns.detail.inRange" />}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.attemptedTip' })}
          value={row.codes_attempted}
          segments={[
            {
              id: 'not-attempted',
              value: attemptColumn - row.codes_attempted,
              className: DROPPED_CLASS_NAME,
            },
            { id: 'attempted', value: row.codes_attempted, className: 'bg-info-main' },
          ]}
        />

        <FunnelColumn
          columnValue={convertColumn}
          data-tst="funnel-row-converted"
          heightPct={share(convertColumn)}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.converted" />}
          ratePct={funnelRatePct(row.codes_converted, row.codes_attempted)}
          scope={<FormattedMessage id="admin.partnerDetail.campaigns.detail.inRange" />}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.convertedTip' })}
          value={row.codes_converted}
          segments={[
            {
              id: 'not-converted',
              value: convertColumn - row.codes_converted,
              className: DROPPED_CLASS_NAME,
            },
            { id: 'converted', value: row.codes_converted, className: 'bg-primary-main' },
          ]}
        />
      </div>

      {/* Beside the chart rather than under it — the columns are capped, so the
          rest of the card's width is otherwise dead space. */}
      <div className="flex flex-col gap-1">
        <LegendDot
          className="bg-text-disabled"
          label={<FormattedMessage id="admin.partnerDetail.campaigns.detail.touched" />}
          value={touched}
        />
        <LegendDot
          className={DROPPED_CLASS_NAME}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.detail.unused" />}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.unusedTip' })}
          value={row.codes_unused}
        />
        {/* Dot-less: `codes_abandoned` is a subset of the converted column's
            drop-off (which also holds still-live and expired-outside-range
            reservations) and in-flight is a live snapshot, so neither maps onto
            a stack segment one-for-one. */}
        <Note
          count={row.codes_abandoned}
          messageId="admin.partnerDetail.campaigns.detail.abandonedNote"
          tooltipId="admin.partnerDetail.campaigns.detail.abandonedTip"
        />
        <Note
          count={row.codes_in_flight}
          messageId="admin.partnerDetail.campaigns.detail.inFlightNote"
          tooltipId="admin.partnerDetail.campaigns.detail.inFlightTip"
        />
      </div>

      <UnbilledRedemptions
        partnerTierMissing={partnerTierMissing}
        row={row}
      />
    </div>
  )
}
